/**
 * ADR-0514: Timer Registry and Navigation Cleanup
 *
 * Wraps setTimeout / setInterval so all active timer handles are tracked.
 * The router calls timers.clearAll() at the start of every route transition
 * to prevent stale callbacks firing on the wrong page.
 *
 * Usage:
 *   import { timers } from "../timers";
 *   // or via window global: window.WMUTimers.setTimeout(fn, 300)
 *
 *   // instead of: const id = setTimeout(fn, 300);
 *   const id = timers.setTimeout(fn, 300);
 *
 *   // instead of: clearTimeout(id);
 *   timers.clear(id);
 *
 *   // router cleanup:
 *   timers.clearAll();
 */

export type WMUTimersAPI = {
  /** Tracked version of window.setTimeout. Clears itself from the set on fire. */
  setTimeout(fn: () => void, ms: number): number;
  /** Tracked version of window.setInterval. */
  setInterval(fn: () => void, ms: number): number;
  /** Clear a specific timer handle (works for both timeout and interval). */
  clear(id: number): void;
  /** Clear ALL tracked timers. Called by the router on navigation. */
  clearAll(): void;
  /** Number of currently active timers (useful for tests). */
  activeCount(): number;
};

const handles = new Set<number>();

const api: WMUTimersAPI = {
  setTimeout(fn: () => void, ms: number): number {
    const id = window.setTimeout(() => {
      handles.delete(id);
      fn();
    }, ms) as unknown as number;
    handles.add(id);
    return id;
  },

  setInterval(fn: () => void, ms: number): number {
    const id = window.setInterval(fn, ms) as unknown as number;
    handles.add(id);
    return id;
  },

  clear(id: number): void {
    handles.delete(id);
    window.clearTimeout(id);
    window.clearInterval(id);
  },

  clearAll(): void {
    handles.forEach((id) => {
      window.clearTimeout(id);
      window.clearInterval(id);
    });
    handles.clear();
  },

  activeCount(): number {
    return handles.size;
  },
};

window.WMUTimers = api;

/** Named export for TypeScript modules. */
export const timers: WMUTimersAPI = api;
